import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Chip from "@material-ui/core/Chip";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Typography, Grid, Box } from "@material-ui/core";
import { format, parseISO } from "date-fns";
import Chart from "react-apexcharts";
import * as XLSX from 'xlsx';
import moment from "moment";
import ModernDatePicker from "../../components/ModernDatePicker";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: "12px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  },
  chartPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: "12px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  },
  exportButton: {
    backgroundColor: "#4ec24e",
    color: "white",
    "&:hover": {
      backgroundColor: "#45ad45",
    },
  },
  filterButton: {
    backgroundColor: "#6517ab",
    color: "white",
    "&:hover": {
      backgroundColor: "#5a1499",
    },
  }
}));

const UserRatingsReport = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [ratings, setRatings] = useState([]);
  const [initialDate, setInitialDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [finalDate, setFinalDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [usersOptions, setUsersOptions] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsersOptions(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchUsers();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/user-ratings", {
        params: {
          startDate: initialDate,
          endDate: finalDate,
          userIds: JSON.stringify(selectedUsers.map(u => u.id)),
        },
      });
      setRatings(data);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchReport();
  };

  const exportToExcel = () => {
    const dataToExport = ratings.map(r => ({
      "Ticket ID": r.ticketId,
      "Contato": r.contactName,
      "Atendente": r.userName,
      "Nota": r.rate,
      "Data": format(parseISO(r.createdAt), "dd/MM/yyyy HH:mm:ss")
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Avaliações");
    XLSX.writeFile(wb, "relatorio-avaliacoes.xlsx");
  };

  const getChartData = () => {
    const distribution = [0, 0, 0, 0, 0];
    ratings.forEach(r => {
      if (r.rate >= 1 && r.rate <= 5) {
        distribution[r.rate - 1]++;
      }
    });

    return {
      series: [{
        name: 'Quantidade',
        data: distribution
      }],
      options: {
        chart: {
          type: 'bar',
          height: 350
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: false,
            columnWidth: '55%',
            distributed: true,
          }
        },
        dataLabels: {
          enabled: true
        },
        colors: ['#FF4560', '#FEB019', '#FFD700', '#00E396', '#008FFB'],
        xaxis: {
          categories: ['1 Estrela', '2 Estrelas', '3 Estrelas', '4 Estrelas', '5 Estrelas'],
        },
        title: {
          text: 'Distribuição de Notas',
          align: 'center'
        }
      }
    };
  };

  const getUserAvgChartData = () => {
    const userRatings = {};
    ratings.forEach(r => {
      if (!userRatings[r.userName]) {
        userRatings[r.userName] = { sum: 0, count: 0 };
      }
      userRatings[r.userName].sum += r.rate;
      userRatings[r.userName].count++;
    });

    const categories = Object.keys(userRatings);
    const seriesData = categories.map(cat => (userRatings[cat].sum / userRatings[cat].count).toFixed(2));

    return {
      series: [{
        name: 'Média de Nota',
        data: seriesData
      }],
      options: {
        chart: {
          type: 'bar',
          height: 350
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: true,
          }
        },
        xaxis: {
          categories: categories,
          max: 5
        },
        title: {
          text: 'Média de Avaliação por Atendente',
          align: 'center'
        },
        colors: ['#6517ab']
      }
    };
  };

  const chartData = getChartData();
  const userAvgChartData = getUserAvgChartData();

  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatório de Avaliações ({ratings.length})</Title>
      </MainHeader>

      <Paper className={classes.filterContainer} variant="outlined">
        <ModernDatePicker
          label="Data Inicial"
          value={initialDate ? moment(initialDate).toDate() : null}
          onChange={(newValue) => setInitialDate(moment(newValue).format("YYYY-MM-DD"))}
        />
        <ModernDatePicker
          label="Data Final"
          value={finalDate ? moment(finalDate).toDate() : null}
          onChange={(newValue) => setFinalDate(moment(newValue).format("YYYY-MM-DD"))}
        />
        <Autocomplete
          multiple
          size="small"
          options={usersOptions}
          getOptionLabel={(option) => option.name}
          value={selectedUsers}
          onChange={(e, v) => setSelectedUsers(v)}
          style={{ width: 300 }}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" label="Filtrar por Usuários" placeholder="Usuários" />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option.name}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
        />
        <Button
          variant="contained"
          className={classes.filterButton}
          onClick={handleSearch}
          startIcon={<SearchIcon />}
        >
          Filtrar
        </Button>
        <Button
          variant="contained"
          className={classes.exportButton}
          onClick={exportToExcel}
          startIcon={<GetAppIcon />}
        >
          Exportar Excel
        </Button>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper className={classes.chartPaper} variant="outlined">
            <Chart
              options={chartData.options}
              series={chartData.series}
              type="bar"
              height={320}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper className={classes.chartPaper} variant="outlined">
            <Chart
              options={userAvgChartData.options}
              series={userAvgChartData.series}
              type="bar"
              height={320}
            />
          </Paper>
        </Grid>
      </Grid>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Ticket ID</TableCell>
              <TableCell align="center">Contato</TableCell>
              <TableCell align="center">Atendente</TableCell>
              <TableCell align="center">Nota</TableCell>
              <TableCell align="center">Data</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ratings.map((rating) => (
              <TableRow key={rating.id}>
                <TableCell align="center">#{rating.ticketId}</TableCell>
                <TableCell align="center">{rating.contactName}</TableCell>
                <TableCell align="center">{rating.userName}</TableCell>
                <TableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center">
                     <Typography style={{ fontWeight: 700, color: "#6517ab", marginRight: 4 }}>{rating.rate}</Typography>
                     <Box color={rating.rate >= 4 ? "#00E396" : rating.rate >= 3 ? "#FFD700" : "#FF4560"}>★</Box>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {format(parseISO(rating.createdAt), "dd/MM/yyyy HH:mm:ss")}
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={5} />}
            {!loading && ratings.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">Nenhuma avaliação encontrada para o período selecionado.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default UserRatingsReport;
